import {
  CALENDAR_UNITS,
  CHECKLIST_ITEM_KINDS,
  INTERVAL_BASES,
  JOB_PRIORITIES,
  JOB_TYPES,
  parseJobDefinitionInput,
  WORK_TYPES,
} from '@felix/domain';
import { z } from 'zod';
import type { NumberField } from './equipment-form.js';
import type { CalendarUnit, JobInput, JobView } from './types.js';

/**
 * Job Card formu (React Hook Form + Zod; plan M7 Task 14a; PMS belgesi §8–§10). Kurallar domain'in
 * `jobDefinitionInputSchema`'sından gelir: sunucu aynı şemayla yeniden denetler, burada kullanıcıya
 * erken söylenir. Hata iletisi domain kodudur (`pms.fieldErrors.<KOD>`).
 */

export interface JobFormValues {
  equipmentId: string;
  code: string;
  title: string;
  workType: (typeof WORK_TYPES)[number];
  jobType: (typeof JOB_TYPES)[number];
  priority: (typeof JOB_PRIORITIES)[number];
  calendarEvery: NumberField;
  calendarUnit: CalendarUnit;
  runningHours: NumberField;
  intervalBasis: (typeof INTERVAL_BASES)[number];
  counterEquipmentId: string | null;
  conditionPointIds: string[];
  procedure: { text: string }[];
  checklist: { text: string; kind: (typeof CHECKLIST_ITEM_KINDS)[number]; unit: string }[];
  safetyItems: { text: string }[];
  acceptanceCriteria: string;
  estimatedMinutes: NumberField;
  responsibleRoleId: string | null;
  instructionDocumentId: string;
  manualDocumentId: string | null;
  manualRef: string;
  requirementRefs: { source: string; reference: string }[];
  plannedSpareParts: { materialId: string; quantity: number }[];
  ceSurvey: boolean;
  alarmTest: boolean;
  baselineDoneOn: string;
  baselineDoneHours: NumberField;
  /** Kayıt yokken aralık düzeltmesinin gerekçesi (K11). */
  changeReason: string;
}

export function emptyJobForm(equipmentId: string): JobFormValues {
  return {
    equipmentId,
    code: '',
    title: '',
    workType: 'Inspection',
    jobType: 'preventive',
    priority: 'medium',
    calendarEvery: '',
    calendarUnit: 'month',
    runningHours: '',
    intervalBasis: 'maker',
    counterEquipmentId: null,
    conditionPointIds: [],
    procedure: [],
    checklist: [],
    safetyItems: [],
    acceptanceCriteria: '',
    estimatedMinutes: '',
    responsibleRoleId: null,
    instructionDocumentId: '',
    manualDocumentId: null,
    manualRef: '',
    requirementRefs: [],
    plannedSpareParts: [],
    ceSurvey: false,
    alarmTest: false,
    baselineDoneOn: '',
    baselineDoneHours: '',
    changeReason: '',
  };
}

const oneOf = <T extends readonly string[]>(values: T, value: string, fallback: T[number]) =>
  (values as readonly string[]).includes(value) ? (value as T[number]) : fallback;

export function jobFormOf(job: JobView): JobFormValues {
  return {
    equipmentId: job.equipmentId,
    code: job.code,
    title: job.title,
    workType: oneOf(WORK_TYPES, job.workType, 'Inspection'),
    jobType: oneOf(JOB_TYPES, job.jobType, 'preventive'),
    priority: oneOf(JOB_PRIORITIES, job.priority, 'medium'),
    calendarEvery: job.interval.calendar?.every ?? '',
    calendarUnit: job.interval.calendar?.unit ?? 'month',
    runningHours: job.interval.runningHours ?? '',
    intervalBasis: oneOf(INTERVAL_BASES, job.intervalBasis, 'maker'),
    counterEquipmentId: job.counterEquipmentId,
    conditionPointIds: [...job.interval.conditionPointIds],
    procedure: (job.procedure ?? []).map((text) => ({ text })),
    checklist: (job.checklist ?? []).map((item) => ({
      text: item.text,
      kind: item.kind,
      unit: item.unit ?? '',
    })),
    safetyItems: (job.safetyItems ?? []).map((text) => ({ text })),
    acceptanceCriteria: job.acceptanceCriteria ?? '',
    estimatedMinutes: job.estimatedMinutes ?? '',
    responsibleRoleId: job.responsibleRoleId,
    instructionDocumentId: job.instructionDocumentId ?? '',
    manualDocumentId: job.manualDocumentId,
    manualRef: job.manualRef ?? '',
    requirementRefs: (job.requirementRefs ?? []).map((r) => ({ ...r })),
    plannedSpareParts: (job.plannedSpareParts ?? []).map((p) => ({ ...p })),
    ceSurvey: job.ceSurvey === true,
    alarmTest: job.alarmTest === true,
    baselineDoneOn: job.baselineDoneOn ?? '',
    baselineDoneHours: job.baselineDoneHours ?? '',
    changeReason: '',
  };
}

const blank = (value: string) => (value.trim() === '' ? null : value);
const num = (value: NumberField) => (value === '' ? null : value);

/** Formun aralığı (domain biçimi). */
export function intervalOf(
  values: Pick<
    JobFormValues,
    'calendarEvery' | 'calendarUnit' | 'runningHours' | 'conditionPointIds'
  >,
) {
  return {
    calendar:
      values.calendarEvery === ''
        ? null
        : { every: values.calendarEvery, unit: values.calendarUnit },
    runningHours: num(values.runningHours),
    conditionPointIds: [...values.conditionPointIds],
  };
}

/** Formdan sunucunun beklediği girdi (boş metin `null`; sunucu boşlukları yeniden düzenler). */
export function toJobInput(values: JobFormValues): JobInput {
  return {
    code: values.code,
    title: values.title,
    workType: values.workType,
    jobType: values.jobType,
    priority: values.priority,
    interval: intervalOf(values),
    intervalBasis: values.intervalBasis,
    counterEquipmentId: values.counterEquipmentId,
    procedure: values.procedure.map((p) => p.text).filter((p) => p.trim() !== ''),
    checklist: values.checklist
      .filter((item) => item.text.trim() !== '')
      .map((item) => ({ text: item.text, kind: item.kind, unit: blank(item.unit) })),
    safetyItems: values.safetyItems.map((s) => s.text).filter((s) => s.trim() !== ''),
    acceptanceCriteria: blank(values.acceptanceCriteria),
    requirementRefs: values.requirementRefs.map((r) => ({ ...r })),
    responsibleRoleId: values.responsibleRoleId,
    estimatedMinutes: num(values.estimatedMinutes),
    instructionDocumentId: blank(values.instructionDocumentId),
    manualDocumentId: values.manualDocumentId,
    manualRef: blank(values.manualRef),
    plannedSpareParts: values.plannedSpareParts.map((p) => ({ ...p })),
    ceSurvey: values.ceSurvey,
    alarmTest: values.alarmTest,
    baselineDoneOn: blank(values.baselineDoneOn),
    baselineDoneHours: num(values.baselineDoneHours),
  };
}

/** Düzenlemede aralık değişti mi (K11: kayıt yokken gerekçe, kayıttan sonra talep). */
export function intervalChanged(job: JobView, values: JobFormValues): boolean {
  const next = intervalOf(values);
  const before = job.interval;
  return (
    JSON.stringify([next.calendar, next.runningHours]) !==
    JSON.stringify([
      before.calendar ? { every: before.calendar.every, unit: before.calendar.unit } : null,
      before.runningHours,
    ])
  );
}

/** Domain alan yolu → formun alanı. */
function formPath(path: string): (string | number)[] {
  if (path === 'interval' || path.startsWith('interval.calendar')) return ['calendarEvery'];
  if (path.startsWith('interval.runningHours')) return ['runningHours'];
  const parts = path.split('.');
  // Liste maddelerinin metni (`procedure.0` → `procedure.0.text`)
  if ((parts[0] === 'procedure' || parts[0] === 'safetyItems') && parts.length === 2) {
    return [...parts, 'text'];
  }
  return parts;
}

const numberField = z.union([z.number(), z.literal('')]);

/**
 * Formun şeması. `today` geminin yerel günüdür: önceki sistemden gelen son yapılış gelecekte
 * olamaz. `intervalReason`: düzenlemede aralık değiştiyse gerekçe zorunlu (K11).
 */
export function jobFormSchema(options: { today: string; intervalReason: boolean; job?: JobView }) {
  return z
    .object({
      equipmentId: z.string(),
      code: z.string(),
      title: z.string(),
      workType: z.enum(WORK_TYPES),
      jobType: z.enum(JOB_TYPES),
      priority: z.enum(JOB_PRIORITIES),
      calendarEvery: numberField,
      calendarUnit: z.enum(CALENDAR_UNITS),
      runningHours: numberField,
      intervalBasis: z.enum(INTERVAL_BASES),
      counterEquipmentId: z.string().nullable(),
      conditionPointIds: z.array(z.string()),
      procedure: z.array(z.object({ text: z.string() })),
      checklist: z.array(
        z.object({ text: z.string(), kind: z.enum(CHECKLIST_ITEM_KINDS), unit: z.string() }),
      ),
      safetyItems: z.array(z.object({ text: z.string() })),
      acceptanceCriteria: z.string(),
      estimatedMinutes: numberField,
      responsibleRoleId: z.string().nullable(),
      instructionDocumentId: z.string(),
      manualDocumentId: z.string().nullable(),
      manualRef: z.string(),
      requirementRefs: z.array(z.object({ source: z.string(), reference: z.string() })),
      plannedSpareParts: z.array(z.object({ materialId: z.string(), quantity: z.number() })),
      ceSurvey: z.boolean(),
      alarmTest: z.boolean(),
      baselineDoneOn: z.string(),
      baselineDoneHours: numberField,
      changeReason: z.string(),
    })
    .superRefine((values, ctx) => {
      if (values.equipmentId === '') {
        ctx.addIssue({ code: 'custom', message: 'REQUIRED', path: ['equipmentId'] });
      }
      const parsed = parseJobDefinitionInput(toJobInput(values as JobFormValues), {
        today: options.today,
      });
      if (!parsed.ok) {
        for (const issue of parsed.issues) {
          ctx.addIssue({ code: 'custom', message: issue.code, path: formPath(issue.path) });
        }
      }
      const changed = options.job ? intervalChanged(options.job, values as JobFormValues) : true;
      if (options.intervalReason && changed && values.changeReason.trim() === '') {
        ctx.addIssue({ code: 'custom', message: 'REQUIRED', path: ['changeReason'] });
      }
    });
}
